import { useContext } from 'react'
import * as Form from '@/features/form'
import { useLingui } from '@lingui/react/macro'

import Input from '@/components/ui/Input'
import Dropdown from '@/components/ui/Dropdown'
import { useSupportedCountries } from '../hooks/useCountries'
import { FormProps } from '../hooks/useSteps'
import { BillingContext } from '../hooks/useBillingData'
import { CustomerFormData } from '../hooks/useBillingForm'

export function CustomerForm({ ref }: FormProps) {
  const { t } = useLingui()
  const countries = useSupportedCountries()
  const { billing, update } = useContext(BillingContext)

  return (
    <Form.Root<CustomerFormData>
      formRef={ref}
      onSubmit={update}
      values={billing as CustomerFormData}
      defaultValues={billing as CustomerFormData}
    >
      <div className="flex flex-col gap-5 p-2.5">
        <Form.Field
          name="contactName"
          label={`${t`Name`}*`}
          description={t`The name on the invoice.`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              autoComplete="billing name"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="email"
          label={`${t`Email`}*`}
          description={t`The Email we send all your invoices to.`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              autoComplete="billing email"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="companyName"
          label={`${t`Company Name`}*`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              autoComplete="billing organization"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field name="country" label={`${t`Country`}*`}>
          {({ renderProps: { onChange, ...props } }) => (
            <Dropdown
              options={countries}
              placeholder={t`Choose Country`}
              onChange={onChange}
              autoComplete="billing country"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="vat"
          label={`${t`VAT Identification Number`}*`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="line1"
          label={`${t`Address Line 1`}*`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              autoComplete="billing address-line1"
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="line2"
          label={t`Address Line 2`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              autoComplete="billing address-line2"
              {...props}
            />
          )}
        </Form.Field>
        <div className="flex flex-row items-center gap-2">
          <Form.Field
            name="city"
            label={`${t`City`}*`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => onChange(event.target.value)}
                type="text"
                autoComplete="billing city"
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="postalCode"
            label={`${t`Postal Code`}*`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              },
              validate: {
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => onChange(event.target.value)}
                type="text"
                {...props}
              />
            )}
          </Form.Field>
        </div>
      </div>
    </Form.Root>
  )
}
